import { useMemo, useState, type ReactNode } from "react";

import type { ThreadV4ViewSpec, ThreadV4WidgetDock, ThreadV4WidgetKind } from "../../api/threadV4Views";
import DAGPanel from "../../components/panels/DAGPanel";
import DataExplorerPanel from "../../components/panels/DataExplorerPanel";
import Flow360ViewerPanel from "../../components/panels/Flow360ViewerPanel";
import type { ThreadAsset } from "../thread-design-system";
import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "./icons";
import {
  AssetRailWidget,
  BuildWidget,
  CorrelogramWidget,
  DagWidget,
  FlowfieldWidget,
  GeometryWidget,
  MeshWidget,
  NotebookWidget,
  PivotSummaryWidget,
  PlotWidget,
  TableWidget,
  ThreadV4Widget,
  V4Widget,
  rowKey,
  selectedLineageNodeIds,
  type ThreadV4WidgetProps,
} from "./ThreadV4Widgets";

type NotebookProps = Omit<ThreadV4WidgetProps, "widget" | "data" | "selectedRowId" | "onSelectRow" | "onDockWidget" | "projectId" | "persistedProject">;

interface StageProps {
  data: V3WorkbenchData;
  selectedRowId: string | null;
  onSelectRow: (rowId: string) => void;
  notebook: NotebookProps;
  projectId: string;
  persistedProject: boolean;
  projectName: string;
}

type EasyPreviewKind = "cfd" | "data" | "analysis" | "power" | "build" | "notebook";
const PREVIEW_ROOT = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/internal/thread-v4-previews`;
type DefaultSurfaceMode = "table" | "correlogram";
type DefaultFocus = "artifact" | "geometry" | "flowfield" | "dag" | null;

export function DefaultThreadView({ data, selectedRowId, onSelectRow, projectId, persistedProject, projectName }: StageProps) {
  const [surface, setSurface] = useState<DefaultSurfaceMode>("table");
  const [focus, setFocus] = useState<DefaultFocus>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(data.assets[0]?.id ?? null);
  const selectedAsset = useMemo(() => data.assets.find((asset) => asset.id === selectedAssetId) ?? data.assets[0] ?? null, [data.assets, selectedAssetId]);
  const selectedRow = data.rows.find((row) => rowKey(row) === selectedRowId) ?? data.rows[0] ?? null;
  const rows = data.totalRowCount || data.rows.length;
  const hasRows = rows > 0;
  const surfaceTitle = persistedProject ? "Data explorer" : surface === "table" ? "Run table" : "Correlogram";

  const openAsset = (asset: ThreadAsset) => {
    setSelectedAssetId(asset.id);
    setFocus("artifact");
  };

  return (
    <div className={`v4-default ${focus ? "has-focus" : ""}`}>
      <GeometryHintStack data={data} projectName={projectName} persistedProject={persistedProject} hasRows={hasRows} selectedRowLabel={selectedRow?.runId ?? null} onFocus={setFocus} />
      <section className="v4-default-main">
        <div className="v4-default-toolbar">
          <div className="v4-default-title">
            <span>{projectName}</span>
            <strong>{surfaceTitle}</strong>
            <small>{rows} rows · {data.columns.length} columns · {data.assets.length} artifacts</small>
          </div>
          {persistedProject ? (
            <span className="v4-live-badge"><Icons.Link size={12} />live backend</span>
          ) : (
            <div className="v4-seg" aria-label="Default surface">
              <button className={surface === "table" ? "is-active" : ""} type="button" onClick={() => setSurface("table")}>Table</button>
              <button className={surface === "correlogram" ? "is-active" : ""} type="button" onClick={() => setSurface("correlogram")}>Corr</button>
            </div>
          )}
        </div>
        <div className={`v4-default-surface ${persistedProject && !hasRows ? "has-empty-strip" : ""}`}>
          {persistedProject && !hasRows ? <LiveEmptyStrip /> : null}
          {persistedProject ? <RealPanelFrame kind="data"><DataExplorerPanel projectId={projectId} isVisible /></RealPanelFrame> : null}
          {!persistedProject && surface === "table" ? <TableWidget data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} title="Whole table" compact /> : null}
          {!persistedProject && surface === "correlogram" ? <CorrelogramWidget data={data} /> : null}
        </div>
      </section>
      <aside className="v4-default-right">
        {focus ? (
            <DefaultFocusPanel focus={focus} data={data} selectedAsset={selectedAsset} selectedRowId={selectedRowId} projectId={projectId} projectName={projectName} persistedProject={persistedProject} onClose={() => setFocus(null)} />
        ) : (
          <>
            <ArtifactAlbum assets={data.assets} selectedRowId={selectedRowId} selectedAssetId={selectedAsset?.id ?? null} onSelect={openAsset} />
            <FlowfieldHint data={data} onOpen={() => setFocus("flowfield")} />
          </>
        )}
      </aside>
      <DagMinimap data={data} selectedRowId={selectedRowId} onOpen={() => setFocus("dag")} />
    </div>
  );
}

function GeometryHintStack({ data, projectName, persistedProject, hasRows, selectedRowLabel, onFocus }: { data: V3WorkbenchData; projectName: string; persistedProject: boolean; hasRows: boolean; selectedRowLabel: string | null; onFocus: (focus: DefaultFocus) => void }) {
  const geometry = data.provenance.nodes.find((node) => node.type === "geometry");
  const mesh = data.provenance.nodes.find((node) => node.type === "mesh");
  const familyCount = hasRows ? Object.keys(data.families).length : 0;
  const meshLabel = mesh?.label || (persistedProject && !hasRows ? "Waiting for import" : "legacy-beta mesh");
  return (
    <aside className="v4-default-left">
      <button className="v4-geom-hint is-primary" type="button" onClick={() => onFocus("geometry")}>
        <span className="v4-geom-hint-k"><Icons.Cube size={13} />Source geometry</span>
        <strong>{geometry?.label || projectName || "Source geometry"}</strong>
        <span className="v4-geom-sketch" aria-hidden="true"><i /><i /><i /></span>
      </button>
      <button className="v4-geom-hint" type="button" onClick={() => onFocus("geometry")}>
        <span className="v4-geom-hint-k"><Icons.Mesh size={13} />Mesh lineage</span>
        <strong>{meshLabel}</strong>
        <small>{familyCount} families · {data.provenance.representedScale.totalNodes} linked nodes</small>
      </button>
      <button className="v4-geom-hint" type="button" onClick={() => onFocus("dag")}>
        <span className="v4-geom-hint-k"><Icons.Pin size={13} />Current row</span>
        <strong>{selectedRowLabel || "No row selected"}</strong>
        <small>DAG · artifacts · table</small>
      </button>
    </aside>
  );
}

function LiveEmptyStrip() {
  return (
    <div className="v4-live-empty">
      <span className="v4-live-empty-dot" />
      <strong>Live project connected</strong>
      <span>Flow360 rows, artifacts, DAG nodes, and UVF assets will fill this view after import.</span>
    </div>
  );
}

function ArtifactAlbum({ assets, selectedRowId, selectedAssetId, onSelect }: { assets: ThreadAsset[]; selectedRowId: string | null; selectedAssetId: string | null; onSelect: (asset: ThreadAsset) => void }) {
  const orderedAssets = useMemo(
    () => [...assets].sort((a, b) => Number(assetLinkedToRow(b, selectedRowId)) - Number(assetLinkedToRow(a, selectedRowId))),
    [assets, selectedRowId],
  );
  return (
    <section className="v4-artifact-album" aria-label="Artifacts">
      <header className="v4-album-head"><span>Artifacts</span><strong>{assets.length}</strong></header>
      <div className="v4-album-track">
        {orderedAssets.slice(0, 12).map((asset, index) => {
          const linked = assetLinkedToRow(asset, selectedRowId);
          return (
            <button key={asset.id} className={`v4-album-card ${asset.id === selectedAssetId ? "is-selected" : ""} ${linked ? "is-linked" : ""}`} type="button" onClick={() => onSelect(asset)}>
              <span className="v4-album-art"><img src={assetCover(asset, index)} alt="" loading="lazy" /><span data-type={asset.type}>{asset.type}</span></span>
              <span className="v4-album-copy">
                <strong>{asset.label}</strong>
                <small>{asset.source} · {asset.updated}</small>
                <em>{asset.refs.rows?.length ?? 0} rows · {asset.health}{linked ? " · linked" : ""}</em>
              </span>
            </button>
          );
        })}
        {!assets.length ? <div className="v4-album-empty">No artifacts</div> : null}
      </div>
    </section>
  );
}

function assetLinkedToRow(asset: ThreadAsset, selectedRowId: string | null): boolean {
  if (!selectedRowId) return false;
  if (asset.refs.rows?.includes(selectedRowId)) return true;
  return asset.refs.cells?.some((cell) => cell.runId === selectedRowId) ?? false;
}

function FlowfieldHint({ data, onOpen }: { data: V3WorkbenchData; onOpen: () => void }) {
  const uvfCount = data.assets.filter((asset) => asset.type === "uvf").length;
  return (
    <button className="v4-flow-hint" type="button" onClick={onOpen}>
      <span className="v4-flow-mini" aria-hidden="true"><i /><i /><i /></span>
      <span><strong>Flowfield</strong><small>{uvfCount || "pending"} UVF assets</small></span>
      <Icons.Maximize size={13} />
    </button>
  );
}

function DefaultFocusPanel({ focus, data, selectedAsset, selectedRowId, projectId, projectName, persistedProject, onClose }: { focus: DefaultFocus; data: V3WorkbenchData; selectedAsset: ThreadAsset | null; selectedRowId: string | null; projectId: string; projectName: string; persistedProject: boolean; onClose: () => void }) {
  return (
    <section className="v4-focus-panel">
      <button className="v4-focus-close" type="button" onClick={onClose} aria-label="Close focus panel"><Icons.Close size={13} /></button>
      {focus === "geometry" ? <GeometryWidget data={data} title="Source geometry" projectName={projectName} /> : null}
      {focus === "flowfield" && persistedProject ? <RealPanelFrame kind="flowfield"><Flow360ViewerPanel projectId={projectId} branch={data.branch || "main"} rows={data.rows as Record<string, unknown>[]} entityTypes={["case"]} /></RealPanelFrame> : null}
      {focus === "flowfield" && !persistedProject ? <FlowfieldWidget data={data} title="Flowfield" /> : null}
      {focus === "dag" && persistedProject ? <RealPanelFrame kind="dag"><DAGPanel projectId={projectId} /></RealPanelFrame> : null}
      {focus === "dag" && !persistedProject ? <DagWidget data={data} selectedRowId={selectedRowId} title="DAG minimap" meta="full lineage" /> : null}
      {focus === "artifact" ? <ArtifactFocus asset={selectedAsset} /> : null}
    </section>
  );
}

function ArtifactFocus({ asset }: { asset: ThreadAsset | null }) {
  if (!asset) return <V4Widget kind="artifact" kindLabel="Artifact" title="Artifact"><div className="v4-album-empty">No artifact selected</div></V4Widget>;
  return (
    <V4Widget kind="artifact" kindLabel={asset.type} title={asset.label} meta={`${asset.source} · ${asset.updated}`}>
      <div className="v4-artifact-focus">
        <img src={assetCover(asset, 0)} alt="" />
        <div className="v4-artifact-focus-copy">
          <span className={`v4-badge is-${asset.health === "warn" ? "stale" : asset.health}`}><span className="dot" />{asset.health}</span>
          <p>{asset.summary || asset.sub}</p>
          <dl>
            <dt>rows</dt><dd>{asset.refs.rows?.length ?? 0}</dd>
            <dt>cells</dt><dd>{asset.refs.cells?.length ?? 0}</dd>
            <dt>owner</dt><dd>{asset.owner || "Thread"}</dd>
          </dl>
        </div>
      </div>
    </V4Widget>
  );
}

function DagMinimap({ data, selectedRowId, onOpen }: { data: V3WorkbenchData; selectedRowId: string | null; onOpen: () => void }) {
  const nodes = data.provenance.nodes.slice(0, 22);
  const edges = data.provenance.edges.slice(0, 28);
  const selectedNodes = useMemo(() => selectedLineageNodeIds(data, selectedRowId), [data, selectedRowId]);
  const points = new Map(nodes.map((node, index) => [node.id, {
    x: Math.max(10, Math.min(150, (node.position?.x ?? index * 31) / 5 + 16)),
    y: Math.max(14, Math.min(86, (node.position?.y ?? index * 19) / 4 + 18)),
  }]));
  return (
    <button className="v4-dag-minimap" type="button" onClick={onOpen}>
      <span className="v4-minimap-title"><Icons.Dag size={12} />DAG</span>
      <svg viewBox="0 0 168 104" preserveAspectRatio="none" aria-hidden="true">
        {edges.map((edge) => {
          const from = points.get(edge.from);
          const to = points.get(edge.to);
          const lit = selectedNodes.size > 0 && (selectedNodes.has(edge.from) || selectedNodes.has(edge.to));
          return from && to ? <line key={edge.id} className={lit ? "is-lit" : ""} x1={from.x} y1={from.y} x2={to.x} y2={to.y} /> : null;
        })}
        {nodes.map((node, index) => {
          const point = points.get(node.id) ?? { x: 12 + index * 6, y: 28 };
          const selected = selectedNodes.has(node.id);
          return <circle key={node.id} className={selected ? "is-selected" : ""} cx={point.x} cy={point.y} r={selected ? 3.8 : index === 0 ? 3.6 : 2.4} />;
        })}
      </svg>
      <span className="v4-minimap-count">{data.provenance.representedScale.totalNodes} nodes</span>
    </button>
  );
}

function RealPanelFrame({ kind, children }: { kind: string; children: ReactNode }) {
  return <div className={`v4-real-panel is-${kind}`}>{children}</div>;
}

function assetCover(asset: ThreadAsset, index: number): string {
  if (asset.type === "uvf") return previewImage("power");
  if (asset.type === "plot" || asset.type === "correlogram-view") return previewImage("analysis");
  if (asset.type === "table-view" || asset.type === "spreadsheet") return previewImage("data");
  if (asset.type === "notebook" || asset.source === "slack" || asset.source === "email") return previewImage("notebook");
  const covers = [previewImage("cfd"), previewImage("data"), previewImage("analysis"), previewImage("power")];
  return covers[index % covers.length] ?? previewImage("cfd");
}

export function EasyView({ data, onPick, scopeLabel, persistedProject }: { data: V3WorkbenchData; onPick: (id: string) => void; scopeLabel: string; persistedProject: boolean }) {
  const rows = data.totalRowCount || data.rows.length;
  const nodes = data.provenance.representedScale.totalNodes;
  const stale = data.assets.filter((asset) => asset.health === "stale" || asset.health === "warn").length;
  const scopeLower = scopeLabel.toLowerCase();
  const saveCopy = persistedProject ? "saved views ready" : "local-only views";
  const notebookCopy = persistedProject ? "team project · notebook-linked dashboard" : "fixture mode · notebook entries stay local";
  return (
    <div className="v4-easy">
      <div className="v4-easy-hero">
        <div className="v4-easy-title">
          <span className="v4-easy-eyebrow">{scopeLabel} · Thread V4 workbench</span>
          <h1>{data.project?.name || "Tsangpo geometry -> Flow360 intake"}</h1>
          <p>Continuous and gapped CSM variants, run data, saved plots, provenance, and Spool notes in one view system.</p>
          <div className="v4-easy-actions">
            <button className="v4-btn is-solid is-lg" type="button" onClick={() => onPick("data")}><Icons.Table size={14} />Open the table</button>
            <button className="v4-btn is-lg" type="button" onClick={() => onPick("cfd")}><Icons.Cube size={14} />Open CFD view</button>
            <button className="v4-btn is-lg" type="button" onClick={() => onPick("customize")}><Icons.Sparkle size={14} />Make me a view</button>
          </div>
        </div>
        <div className="v4-easy-summary">
          <div className="v4-section-label">{scopeLabel} at a glance</div>
          <div className="v4-easy-stats">
            <div className="v4-easy-stat"><strong>{rows}</strong><span>rows · {data.columns.length} cols</span></div>
            <div className="v4-easy-stat"><strong>{nodes}</strong><span>provenance nodes</span></div>
            <div className={`v4-easy-stat ${stale ? "has-warn" : ""}`}><strong>{stale}</strong><span>stale / refresh</span></div>
          </div>
          <div className="v4-easy-meta">
            <span><Icons.Branch size={12} />{scopeLower} <strong>{data.branch}</strong> · {saveCopy}</span>
            <span><Icons.Users size={12} />{notebookCopy}</span>
            <span><Icons.Github size={12} /><code>qiqi/tsangpo</code><small>d889d19a</small></span>
          </div>
        </div>
      </div>
      <div className="v4-easy-grid">
        <EasyCard icon={<Icons.Cube size={14} />} title="CFD" desc="Geometry, mesh, flowfield, and build state." foot="Standard" metric="4 widgets" onPick={() => onPick("cfd")} preview={<EasyPreview kind="cfd" data={data} />} />
        <EasyCard icon={<Icons.Table size={14} />} title="Data" desc="Runs table, pivots, filters, saved plots, and DAG context." foot="Standard" metric={`${rows} rows`} onPick={() => onPick("data")} preview={<EasyPreview kind="data" data={data} />} />
        <EasyCard icon={<Icons.Plot size={14} />} title="Analysis" desc="Plot grid, selected slice, provenance neighborhood, and evidence rail." foot="Standard" metric="plots" onPick={() => onPick("analysis")} preview={<EasyPreview kind="analysis" data={data} />} />
        <EasyCard icon={<Icons.GridIcon size={14} />} title="Tsangpo CFD review" desc="Geometry, mesh, DAG, table, flowfield, and switchable plot side by side." foot="Custom · shared" metric="6 widgets" onPick={() => onPick("power")} preview={<EasyPreview kind="power" data={data} />} />
        <EasyCard icon={<Icons.Build size={14} />} title="Continue Tsangpo build" desc="Plan and submit campaigns from the build surface." foot="Build surface" metric="alpha sweep" onPick={() => onPick("cfd")} preview={<EasyPreview kind="build" data={data} />} />
        <EasyCard icon={<Icons.Comments size={14} />} title="Spool notebook" desc="Capture notes from people, agents, Slack, Gmail, Flow360, and GitHub." foot="Source-linked" metric="notebook" onPick={() => onPick("data")} preview={<EasyPreview kind="notebook" data={data} />} />
      </div>
    </div>
  );
}

function EasyCard({ icon, title, desc, foot, metric, onPick, preview }: { icon: ReactNode; title: string; desc: string; foot: string; metric: string; onPick: () => void; preview: ReactNode }) {
  return (
    <button className="v4-easy-card" type="button" onClick={onPick}>
      <span className="v4-easy-card-icon">{icon}</span>
      <span className="v4-easy-card-copy"><strong>{title}</strong><p>{desc}</p></span>
      <span className="v4-easy-card-foot"><span>{foot}</span><strong>{metric} -&gt;</strong></span>
      <span className="v4-easy-card-preview">{preview}</span>
    </button>
  );
}

function previewImage(kind: EasyPreviewKind): string {
  if (kind === "cfd" || kind === "build") return `${PREVIEW_ROOT}/v3-table-build-dag.png`;
  if (kind === "data") return `${PREVIEW_ROOT}/v3-table-focus.png`;
  if (kind === "analysis" || kind === "notebook") return `${PREVIEW_ROOT}/v3-dag-review.png`;
  return `${PREVIEW_ROOT}/v3-split-review.png`;
}

function EasyPreview({ kind, data }: { kind: EasyPreviewKind; data: V3WorkbenchData }) {
  const rows = data.totalRowCount || data.rows.length;
  const nodes = data.provenance.representedScale.totalNodes;
  const asset = data.assets.find((item) => {
    const label = `${item.label} ${item.summary ?? ""}`.toLowerCase();
    if (kind === "cfd") return label.includes("csm") || label.includes("geometry");
    if (kind === "analysis") return item.type === "plot";
    if (kind === "notebook") return item.type === "notebook";
    return true;
  }) ?? data.assets[0];
  return (
    <span className={`v4-easy-preview is-${kind} has-real-image`} aria-hidden="true">
      <img className="v4-preview-image" src={previewImage(kind)} alt="" loading="lazy" />
      <span className="v4-preview-image-shade" />
      {kind === "data" ? <RealDataRows data={data} /> : null}
      {kind === "analysis" ? <RealMiniPlot data={data} /> : null}
      {kind === "power" ? <RealDashboardMap data={data} /> : null}
      {kind === "build" ? <RealBuildSteps data={data} /> : null}
      {kind === "notebook" ? <RealSourceRows nodes={nodes} /> : null}
      <span className="v4-preview-real-meta">
        <b>{asset?.label || data.project?.name || "Tsangpo"}</b>
        <small>{rows} rows · {nodes} nodes</small>
      </span>
    </span>
  );
}

function RealDataRows({ data }: { data: V3WorkbenchData }) {
  return (
    <span className="v4-preview-real-table">
      {data.rows.slice(0, 4).map((row) => (
        <span key={row.runId}>
          <b>{String(row.phase ?? row.runId).slice(0, 18)}</b>
          <i>{String(row.geometry ?? row.design ?? "geometry").slice(0, 20)}</i>
          <em>{typeof row.alpha === "number" ? `alpha ${row.alpha}` : row.status}</em>
        </span>
      ))}
    </span>
  );
}

function RealMiniPlot({ data }: { data: V3WorkbenchData }) {
  const series = data.rows
    .filter((row) => typeof row.alpha === "number" && typeof row.CL === "number")
    .sort((a, b) => Number(a.alpha) - Number(b.alpha))
    .slice(0, 12)
    .map((row) => ({ x: Number(row.alpha), y: Number(row.CL) }));
  const xmin = Math.min(...series.map((point) => point.x), 0);
  const xmax = Math.max(...series.map((point) => point.x), 1);
  const ymin = Math.min(...series.map((point) => point.y), 0);
  const ymax = Math.max(...series.map((point) => point.y), 1);
  const d = series.map((point, index) => {
    const x = 14 + ((point.x - xmin) / Math.max(1e-9, xmax - xmin)) * 160;
    const y = 76 - ((point.y - ymin) / Math.max(1e-9, ymax - ymin)) * 50;
    return `${index ? "L" : "M"} ${x} ${y}`;
  }).join(" ");
  return <svg className="v4-preview-real-plot" viewBox="0 0 190 92" preserveAspectRatio="none"><path d={d} /></svg>;
}

function RealDashboardMap({ data }: { data: V3WorkbenchData }) {
  return <span className="v4-preview-real-map">{["geometry", "mesh", "dag", `${data.rows.length} rows`, "flowfield", "CL/CD plot"].map((label) => <i key={label}>{label}</i>)}</span>;
}

function RealBuildSteps({ data }: { data: V3WorkbenchData }) {
  const labels = Array.from(new Set(data.rows.map((row) => String(row.phase ?? row.conditionKey ?? row.runId)))).slice(0, 3);
  return <span className="v4-preview-real-steps">{labels.map((label, index) => <i key={label} className={index === labels.length - 1 ? "is-next" : ""}><b />{label}</i>)}</span>;
}

function RealSourceRows({ nodes }: { nodes: number }) {
  return <span className="v4-preview-real-sources">{["Slack", "Agent", "Flow360"].map((label) => <i key={label}><b>{label}</b><span /></i>)}<em>{nodes} linked records</em></span>;
}

export function CfdView({ data }: StageProps) {
  return (
    <div className="v4-work is-cfd">
      <div className="v4-slot is-build"><BuildWidget data={data} /></div>
      <div className="v4-slot is-geometry"><GeometryWidget data={data} /></div>
      <div className="v4-slot is-mesh"><MeshWidget data={data} /></div>
      <div className="v4-slot is-flow"><FlowfieldWidget data={data} /></div>
    </div>
  );
}

export function DataView({ data, selectedRowId, onSelectRow, notebook }: StageProps) {
  return (
    <div className="v4-work is-data">
      <div className="v4-slot is-table"><TableWidget data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} title="Runs" /></div>
      <div className="v4-slot is-assets"><AssetRailWidget data={data} title="Saved plots" types={["plot", "uvf"]} /></div>
      <div className="v4-slot is-dag"><DagWidget data={data} title="Provenance context" meta="rows -> upstream / downstream" /></div>
      <div className="v4-slot is-notebook"><NotebookWidget {...notebook} /></div>
    </div>
  );
}

export function AnalysisView({ data, notebook }: StageProps) {
  return (
    <div className="v4-work is-analysis">
      <div className="v4-slot is-plot-main"><PlotWidget data={data} title="Plot · CL vs alpha" initialQuantity="CL" /></div>
      <div className="v4-slot is-plot-secondary"><PlotWidget data={data} title="Plot · CD vs alpha" initialQuantity="CD" showTabs={false} /></div>
      <div className="v4-slot is-pivot"><PivotSummaryWidget data={data} /></div>
      <div className="v4-slot is-dag"><DagWidget data={data} title="Analysis DAG" /></div>
      <div className="v4-slot is-evidence"><AssetRailWidget data={data} title="Evidence rail" types={["report", "deck", "uvf", "plot"]} /></div>
      <div className="v4-slot is-notebook"><NotebookWidget {...notebook} /></div>
    </div>
  );
}

export function PowerDashboard({ data, selectedRowId, onSelectRow }: StageProps) {
  return (
    <div className="v4-work is-power">
      <div className="v4-slot is-geometry"><GeometryWidget data={data} title="Geometry" /></div>
      <div className="v4-slot is-mesh"><MeshWidget data={data} title="Mesh" /></div>
      <div className="v4-slot is-dag"><DagWidget data={data} title="Provenance" meta="rows neighborhood · depth ±2" /></div>
      <div className="v4-slot is-table"><TableWidget data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} title="Runs · all rows" compact /></div>
      <div className="v4-slot is-flow"><FlowfieldWidget data={data} title="Flowfield · selected run" /></div>
      <div className="v4-slot is-plot"><PlotWidget data={data} title="Plot · switcher" /></div>
    </div>
  );
}

export function CustomizeFlow({
  data,
  selectedRowId,
  onSelectRow,
  notebook,
  projectId,
  persistedProject,
  draftView,
  onAddWidget,
  onAutoArrange,
  onDockWidget,
  onToggleWidgetLock,
  onNameChange,
  onSave,
  onClose,
}: StageProps & {
  draftView: ThreadV4ViewSpec | null;
  onAddWidget: (kind: ThreadV4WidgetKind) => void;
  onAutoArrange: () => void;
  onDockWidget: (widgetId: string, dock: ThreadV4WidgetDock) => void;
  onToggleWidgetLock: (widgetId: string) => void;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <div className="v4-work is-customize">
      <div className="v4-slot is-palette"><AddWidgetMenu onAdd={onAddWidget} /></div>
      <div className="v4-slot is-editor">
        {draftView ? (
          <div className="v4-editor-live">
            <CustomLayoutView view={draftView} data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} notebook={notebook} projectId={projectId} persistedProject={persistedProject} onDockWidget={onDockWidget} onToggleWidgetLock={onToggleWidgetLock} />
          </div>
        ) : (
          <DropZone onAdd={onAddWidget} />
        )}
      </div>
      <div className="v4-slot is-inspector"><ViewInspector name={draftView?.name ?? "Untitled view"} widgetCount={draftView?.layout_json.widgets.length ?? 0} onNameChange={onNameChange} onAutoArrange={onAutoArrange} onSave={onSave} onClose={onClose} /></div>
    </div>
  );
}

function AddWidgetMenu({ onAdd }: { onAdd: (kind: ThreadV4WidgetKind) => void }) {
  const widgets: Array<readonly [ThreadV4WidgetKind, string, string, ReactNode]> = [
    ["geometry", "Geometry", "CSM source render", <Icons.Cube size={14} />],
    ["mesh", "Mesh", "Surface / volume / slice", <Icons.Mesh size={14} />],
    ["flowfield", "Flowfield", "UVF · Cp/u/turb", <Icons.Flowfield size={14} />],
    ["table", "Table", "Runs · pivot · filter", <Icons.Table size={14} />],
    ["dag", "DAG", "Provenance neighborhood", <Icons.Dag size={14} />],
    ["plot", "Plot", "Single switchable plot", <Icons.Plot size={14} />],
    ["notebook", "Notebook", "People · agents · sources", <Icons.Comments size={14} />],
  ] as const;
  return <div className="v4-palette"><h4>Add widget</h4><div className="v4-palette-list">{widgets.map(([id, label, sub, icon]) => <button key={id} className="v4-palette-item" type="button" onClick={() => onAdd(id)}><span className="v4-palette-icon">{icon}</span><span><strong>{label}</strong><small>{sub}</small></span><Icons.Plus size={12} className="drag-handle" /></button>)}</div></div>;
}

function DropZone({ onAdd }: { onAdd: (kind: ThreadV4WidgetKind) => void }) {
  return <div className="v4-dropzone"><div><Icons.Plus size={16} /></div><strong>Start a custom view</strong><small>Pick real Thread widgets from the palette, then auto-arrange or save.</small><span><button className="v4-btn is-sm" type="button" onClick={() => onAdd("geometry")}><Icons.Cube size={12} />Geometry</button><button className="v4-btn is-sm" type="button" onClick={() => onAdd("dag")}><Icons.Dag size={12} />DAG</button></span></div>;
}

function ViewInspector({ name, widgetCount, onNameChange, onAutoArrange, onSave, onClose }: { name: string; widgetCount: number; onNameChange: (name: string) => void; onAutoArrange: () => void; onSave: () => void; onClose: () => void }) {
  return (
    <V4Widget kind="md" kindLabel="View" title="View settings" meta="thread.view.v1" actions={<><button className="v4-wh-iconbtn" type="button"><Icons.Reset size={13} /></button><button className="v4-wh-iconbtn" type="button" onClick={onClose}><Icons.Close size={13} /></button></>}>
      <div className="v4-inspector">
        <label className="v4-field"><span>name</span><input className="v4-input" value={name} onChange={(event) => onNameChange(event.currentTarget.value)} /></label>
        <div className="v4-field"><span>auto-arrange</span><div className="v4-seg"><button className="is-active" type="button" onClick={onAutoArrange}>Power</button><button type="button" onClick={onAutoArrange}>Stack</button></div></div>
        <div className="v4-field"><span>binding</span><div className="v4-chip-wrap"><span className="v4-chip is-brand"><Icons.Branch size={11} />branch · main</span><span className="v4-chip is-pivot"><Icons.Filter size={11} />current filter</span></div></div>
        <div className="v4-field"><span>widgets</span><strong>{widgetCount}</strong></div>
        <div className="v4-inspector-actions"><button className="v4-btn is-solid" type="button" onClick={onSave}><Icons.Save size={13} />Save view...</button><button className="v4-btn" type="button"><Icons.Share size={13} />Share snapshot</button><button className="v4-btn" type="button" onClick={onClose}><Icons.Eraser size={13} />Close editor</button></div>
      </div>
    </V4Widget>
  );
}

export function CustomLayoutView({ view, data, selectedRowId, onSelectRow, notebook, projectId, persistedProject, onDockWidget, onToggleWidgetLock }: { view: ThreadV4ViewSpec; data: V3WorkbenchData; selectedRowId: string | null; onSelectRow: (rowId: string) => void; notebook: NotebookProps; projectId: string; persistedProject: boolean; onDockWidget?: (widgetId: string, dock: ThreadV4WidgetDock) => void; onToggleWidgetLock?: (widgetId: string) => void }) {
  const layout = view.layout_json;
  return (
    <div className="v4-custom-layout" style={{ gridTemplateColumns: `repeat(${layout.cols || 12}, minmax(0, 1fr))`, gridAutoRows: `${layout.row_height || 128}px` }}>
      {layout.widgets.map((widget) => <ThreadV4Widget key={widget.id} widget={widget} data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} projectId={projectId} persistedProject={persistedProject} onDockWidget={onDockWidget} onToggleWidgetLock={onToggleWidgetLock} {...notebook} />)}
    </div>
  );
}
